import React from 'react';
import PropTypes from 'prop-types';
import { ExternalLink, Pencil } from 'lucide-react';
import { MONTHS, USER_EVENT_COLOR } from './constants';
import { getImportantDateColor } from './eventColors';
import { fmtShort, utcCalendarIsoToLocalMidnight } from './calendarUtils';

export default function EventCard({
  event,
  type = 'importantDate',
  colorIdx = 0,
  onEdit
}) {
  const isCalendarEvent = type === 'calendarEvent';
  const color = isCalendarEvent
    ? USER_EVENT_COLOR
    : getImportantDateColor(event, colorIdx);

  const start = isCalendarEvent
    ? new Date(event.startTime)
    : utcCalendarIsoToLocalMidnight(event.calendarDate);

  let end;
  if (isCalendarEvent) {
    end = event.endTime ? new Date(event.endTime) : start;
  } else {
    end = new Date(event.endDate);
  }

  const handleClick = () => {
    if (isCalendarEvent && onEdit) onEdit(event);
  };

  const editButton =
    isCalendarEvent && onEdit ? (
      <button
        type='button'
        className='shrink-0 flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border-0 bg-transparent cursor-pointer'
        style={{ backgroundColor: color.bg, color: color.text }}
        onClick={e => {
          e.stopPropagation();
          onEdit(event);
        }}
      >
        Edit <Pencil className='w-3 h-3' />
      </button>
    ) : null;

  const cardContent = (
    <>
      <div
        className='flex h-10 w-10 shrink-0 flex-col items-center justify-center rounded-lg text-center sm:h-11 sm:w-11'
        style={{ backgroundColor: color.bg }}
      >
        <span
          className='text-[9px] font-bold uppercase tracking-wider'
          style={{ color: color.text }}
        >
          {MONTHS[start.getMonth()].slice(0, 3)}
        </span>
        <span
          className='text-base font-black leading-none'
          style={{ color: color.text }}
        >
          {String(start.getDate()).padStart(2, '0')}
        </span>
      </div>

      <div className='min-w-0 flex-1 pr-5 flex flex-col gap-2'>
        <div className='flex min-w-0 flex-col gap-1.5 sm:flex-row sm:items-start sm:gap-2'>
          <p className='min-w-0 m-0 text-sm font-semibold text-slate-800 wrap-break-word leading-none'>
            {event.title}
          </p>
          <div className='flex shrink-0 flex-wrap items-center gap-1.5'>
            {!isCalendarEvent && event.textURL && (
              <a
                href={event.textURL}
                target='_blank'
                rel='noreferrer noopener'
                className='inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold'
                style={{ backgroundColor: color.bg, color: color.text }}
                onClick={e => e.stopPropagation()}
              >
                View <ExternalLink className='h-3 w-3' />
              </a>
            )}
            {editButton}
          </div>
        </div>
        {!isCalendarEvent && event.subheader && (
          <p className='m-0 text-[11px]  text-slate-500 wrap-break-word leading-none'>
            {event.subheader}
          </p>
        )}
        {isCalendarEvent && (
          <p className='m-0 text-[11px] text-slate-500 wrap-break-word leading-none'>
            {event.description || '—'}
          </p>
        )}
        <p className='m-0 text-[10px] leading-snug text-slate-400'>
          {fmtShort(start)} → {fmtShort(end)}
        </p>
      </div>

      <div
        className='absolute right-2.5 top-2.5 h-2 w-2 shrink-0 rounded-full sm:right-3 sm:top-3'
        style={{ backgroundColor: color.dot }}
        aria-hidden
      />
    </>
  );

  const baseClassName =
    'relative flex items-start gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-xl border transition-all duration-200 min-w-0 w-full text-left';
  const style = { borderColor: color.bg, backgroundColor: '#fff' };

  if (isCalendarEvent && onEdit) {
    return (
      <button
        type='button'
        className={baseClassName + ' cursor-pointer'}
        style={style}
        onClick={handleClick}
      >
        {cardContent}
      </button>
    );
  }

  return (
    <div className={baseClassName} style={style}>
      {cardContent}
    </div>
  );
}

EventCard.propTypes = {
  event: PropTypes.object.isRequired,
  type: PropTypes.oneOf(['importantDate', 'calendarEvent']),
  colorIdx: PropTypes.number,
  onEdit: PropTypes.func
};
